import { fetchBaseQuery } from '@reduxjs/toolkit/dist/query';
import { Mutex } from 'async-mutex';
import { selectClientId, selectRefreshToken } from 'redux/user/user.selectors';
import { userActions } from 'redux/user/user.slice';
import { getEnv } from 'util/getEnv';
import { prepareHeaders } from './inc-api-headers';

const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
  baseUrl: getEnv('REACT_APP_ADMIN_API_URL'),
  prepareHeaders,
});

const baseQueryWithTimeout = (args, api, extraOptions) =>
  Promise.race([
    new Promise(resolve =>
      setTimeout(
        () => resolve({ error: 'timed out' }),
        extraOptions?.timeout ?? getEnv('REACT_APP_TIMEOUT'),
      ),
    ),
    baseQuery(args, api, extraOptions),
  ]);

export const baseQueryWithReauth = async (args, api, extraOptions) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();
  let result = await baseQueryWithTimeout(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    // checking whether the mutex is locked
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshToken = selectRefreshToken(api.getState());
        const refreshResult =
          !!refreshToken &&
          (await baseQueryWithTimeout(
            {
              url: 'oauth/token',
              method: 'POST',
              headers: {
                authorization: 'Basic Y2xpZW50YXBwOjEyMzQ1Ng==',
                'content-type': 'application/x-www-form-urlencoded',
              },
              body: Object.entries({
                refresh_token: refreshToken,
                grant_type: 'refresh_token',
                scope: 'read write',
                client_id: selectClientId(api.getState()),
              })
                .map(
                  ([key, value]) =>
                    `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
                )
                .join('&'),
            },
            api,
            { keepUnusedDataFor: 0 },
          ));
        if (!!refreshResult?.data) {
          api.dispatch(userActions.refreshToken(refreshResult.data));
          // retry the initial query
          result = await baseQueryWithTimeout(args, api, extraOptions);
        } else {
          api.dispatch(userActions.logout());
        }
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      result = await baseQueryWithTimeout(args, api, extraOptions);
    }
  }
  return result;
};
